import React, { useState, useEffect, useMemo } from 'react';

const styles = {
  container: {
    display: 'flex',
    gap: '32px',
    alignItems: 'flex-start',
    width: '100%',
    animation: 'fadeUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) both',
  },
  previewSection: {
    flex: 1,
    background: 'rgba(240, 242, 245, 0.6)',
    borderRadius: '24px',
    padding: '40px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '520px',
    border: '1px solid rgba(255, 255, 255, 0.4)',
    boxShadow: 'inset 0 0 40px rgba(0, 0, 0, 0.02)',
    overflow: 'hidden',
  },
  previewHeader: {
    position: 'absolute',
    top: '20px',
    left: '20px',
    right: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    pointerEvents: 'none',
  },
  badge: {
    padding: '6px 12px',
    borderRadius: '99px',
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    background: 'rgba(226, 232, 240, 0.8)',
    color: '#64748b',
    backdropFilter: 'blur(4px)',
  },
  pageInfo: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#94a3b8',
  },
  docFrame: {
    background: '#fff',
    padding: '12px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
    borderRadius: '4px',
    position: 'relative',
    transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
    maxWidth: '90%',
    maxHeight: '400px',
  },
  docPlaceholder: {
    width: '280px',
    height: '380px',
    background: '#1e293b', // Dark background like in screenshot
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'rgba(255,255,255,0.1)',
    fontSize: '40px',
    borderRadius: '2px',
    overflow: 'hidden',
    position: 'relative',
  },
  docContentMock: {
    // This is to simulate the text in the screenshot
    padding: '30px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
    height: '100%',
    opacity: 0.8,
  },
  line: (width) => ({
    height: '4px',
    width: width,
    background: 'currentColor',
    borderRadius: '2px',
  }),
  plusBtn: {
    position: 'absolute',
    top: '-15px',
    right: '-15px',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: '#b91c1c', // Crimson red
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(185, 28, 28, 0.3)',
    border: 'none',
    zIndex: 10,
  },
  privateBadge: {
    position: 'absolute',
    bottom: '20px',
    left: '20px',
    background: '#86efac', // Light green
    color: '#065f46', // Dark green
    padding: '6px 14px',
    borderRadius: '999px',
    fontSize: '10px',
    fontWeight: 800,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  },
  sidebar: {
    width: '320px',
    background: '#fff',
    borderRadius: '24px',
    padding: '32px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  sidebarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sidebarTitle: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#1e293b',
  },
  resetBtn: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#166534',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  btnRow: {
    display: 'flex',
    gap: '16px',
  },
  rotateBtn: {
    flex: 1,
    height: '100px',
    borderRadius: '16px',
    background: '#f1f5f9',
    border: 'none',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    cursor: 'pointer',
    transition: 'background 0.2s, transform 0.1s',
    color: '#475569',
  },
  btnIcon: {
    fontSize: '24px',
  },
  btnLabel: {
    fontSize: '10px',
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  statusArea: {
    borderTop: '1px solid #f1f5f9',
    paddingTop: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  statusRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13px',
    fontWeight: 500,
  },
  statusLabel: {
    color: '#64748b',
  },
  statusValue: {
    color: '#1e293b',
    fontWeight: 600,
  },
  readyValue: {
    color: '#16a34a',
    fontWeight: 700,
  },
  downloadBtn: {
    marginTop: '10px',
    background: '#365314', // Dark olive green
    color: '#fff',
    height: '52px',
    borderRadius: '12px',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.2s, transform 0.1s',
    boxShadow: '0 10px 15px -3px rgba(54, 83, 20, 0.3)',
  },
};

export default function RotationEditor({ file, onConvert, status }) {
  const [rotation, setRotation] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [file]);

  const handleRotate = (dir) => {
    setRotation(prev => {
      let next = prev + (dir === 'right' ? 90 : -90);
      if (next < 0) next = 270;
      if (next >= 360) next = 0;
      return next;
    });
  };

  const handleReset = () => setRotation(0);

  return (
    <div style={styles.container}>
      <div style={styles.previewSection}>
        <div style={styles.previewHeader}>
          <div style={styles.badge}>Preview Mode</div>
          <div style={styles.pageInfo}>Page 1 of 1</div>
        </div>

        <div style={{ ...styles.docFrame, transform: `rotate(${rotation}deg)` }}>
          <button style={styles.plusBtn}>+</button>
          
          <div style={styles.docPlaceholder}>
            {previewUrl ? (
              <iframe 
                src={previewUrl + '#toolbar=0&navpanes=0&scrollbar=0'} 
                title="PDF Preview"
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            ) : (
              <div style={styles.docContentMock}>
                <div style={styles.line('40%')} />
                <div style={styles.line('80%')} />
                <div style={styles.line('60%')} />
                <div style={{ ...styles.line('90%'), marginTop: '20px' }} />
                <div style={styles.line('85%')} />
                <div style={styles.line('70%')} />
              </div>
            )}
          </div>
        </div>

        <div style={styles.privateBadge}>
          <span style={{ fontSize: '12px' }}>🛡️</span>
          100% PRIVATE UTILITY
        </div>
      </div>

      <div style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <div style={styles.sidebarTitle}>Rotation</div>
          <button style={styles.resetBtn} onClick={handleReset}>Reset All</button>
        </div>

        <div style={styles.btnRow}>
          <button 
            style={styles.rotateBtn} 
            onClick={() => handleRotate('left')}
            onMouseDown={(e) => e.target.style.transform = 'scale(0.96)'}
            onMouseUp={(e) => e.target.style.transform = 'scale(1)'}
          >
            <div style={styles.btnIcon}>↺</div>
            <div style={styles.btnLabel}>Left</div>
          </button>
          <button 
            style={styles.rotateBtn} 
            onClick={() => handleRotate('right')}
            onMouseDown={(e) => e.target.style.transform = 'scale(0.96)'}
            onMouseUp={(e) => e.target.style.transform = 'scale(1)'}
          >
            <div style={styles.btnIcon}>↻</div>
            <div style={styles.btnLabel}>Right</div>
          </button>
        </div>

        <div style={styles.statusArea}>
          <div style={styles.statusRow}>
            <span style={styles.statusLabel}>Current Rotation:</span>
            <span style={styles.statusValue}>{rotation}°</span>
          </div>
          <div style={styles.statusRow}>
            <span style={styles.statusLabel}>Status:</span>
            <span style={styles.readyValue}>Ready</span>
          </div>
        </div>

        <button 
          style={{ 
            ...styles.downloadBtn, 
            opacity: status === 'converting' ? 0.7 : 1,
            pointerEvents: status === 'converting' ? 'none' : 'auto'
          }} 
          onClick={() => onConvert(rotation)}
        >
          {status === 'converting' ? (
            'Processing...'
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download PDF
            </>
          )}
        </button>
      </div>
    </div>
  );
}
